import { useState } from 'react';
import { Brain, Send, Loader as Loader2, ChevronDown, ChevronUp, Clock } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useCompany } from '@/context/CompanyContext';
import { useAuth } from '@/features/auth';

interface ReportResult {
  id: string;
  prompt: string;
  generated_sql: string;
  results: Record<string, unknown>[] | null;
  error: string | null;
  status: 'pending' | 'success' | 'error';
  created_at: string;
}

const EXAMPLE_PROMPTS = [
  'הצג את 10 המוצרים הנמכרים ביותר החודש',
  'כמה הכנסות היו לי השבוע לפי קטגוריה?',
  'אילו לקוחות ביצעו הזמנות בשלושים הימים האחרונים?',
  'מה המצב הנוכחי של עסקאות הבנק שטרם קוטלגו?',
];

export function AIReportsPage() {
  const { activeCompanyId } = useCompany();
  const { user } = useAuth();

  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<ReportResult[]>([]);
  const [expandedSql, setExpandedSql] = useState<Record<string, boolean>>({});

  const runReport = async (queryPrompt: string) => {
    if (!queryPrompt.trim() || !activeCompanyId || !user) return;

    setLoading(true);
    setPrompt('');

    const tempId = crypto.randomUUID();
    const pending: ReportResult = {
      id: tempId,
      prompt: queryPrompt,
      generated_sql: '',
      results: null,
      error: null,
      status: 'pending',
      created_at: new Date().toISOString(),
    };
    setHistory(prev => [pending, ...prev]);

    try {
      // Create session row
      const { data: session, error: sessionError } = await supabase
        .from('ai_report_sessions')
        .insert({ company_id: activeCompanyId, user_id: user.id, prompt: queryPrompt })
        .select('id')
        .single();

      if (sessionError || !session) throw new Error(sessionError?.message ?? 'Failed to create session');

      const { data: { session: authSession } } = await supabase.auth.getSession();
      const token = authSession?.access_token;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-report-engine`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ prompt: queryPrompt, company_id: activeCompanyId, session_id: session.id }),
        }
      );

      const json = await response.json();

      if (!response.ok || json.error) {
        setHistory(prev => prev.map(r =>
          r.id === tempId
            ? { ...r, id: session.id, status: 'error', error: json.error ?? 'Unknown error', generated_sql: json.generated_sql ?? '' }
            : r
        ));
      } else {
        setHistory(prev => prev.map(r =>
          r.id === tempId
            ? { ...r, id: session.id, status: 'success', generated_sql: json.generated_sql, results: json.results }
            : r
        ));
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setHistory(prev => prev.map(r =>
        r.id === tempId ? { ...r, status: 'error', error: message } : r
      ));
    } finally {
      setLoading(false);
    }
  };

  const getColumns = (results: Record<string, unknown>[]) =>
    results.length > 0 ? Object.keys(results[0]!) : [];

  const formatValue = (val: unknown): string => {
    if (val === null || val === undefined) return '—';
    if (typeof val === 'number') return val.toLocaleString('he-IL');
    if (typeof val === 'boolean') return val ? 'כן' : 'לא';
    return String(val);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-8" dir="rtl">

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
          <Brain className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">דוחות AI</h1>
          <p className="text-sm text-gray-500">שאל שאלה בשפה טבעית וקבל תשובה מנתוני החברה</p>
        </div>
      </div>

      {/* Input */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-4">
        <label className="block text-sm font-medium text-gray-700">מה תרצה לדעת?</label>
        <div className="flex gap-3">
          <textarea
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) runReport(prompt);
            }}
            placeholder="לדוגמה: כמה הזמנות נפתחו החודש?"
            rows={3}
            className="flex-1 resize-none rounded-xl border border-gray-300 px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={loading}
          />
          <button
            onClick={() => runReport(prompt)}
            disabled={loading || !prompt.trim()}
            className="self-end px-5 py-3 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            הפק דוח
          </button>
        </div>

        {/* Example prompts */}
        <div className="space-y-1">
          <p className="text-xs text-gray-400 font-medium">דוגמאות:</p>
          <div className="flex flex-wrap gap-2">
            {EXAMPLE_PROMPTS.map(ex => (
              <button
                key={ex}
                onClick={() => runReport(ex)}
                disabled={loading}
                className="text-xs px-3 py-1.5 rounded-full bg-gray-100 text-gray-600 hover:bg-blue-50 hover:text-blue-700 transition-colors disabled:opacity-50"
              >
                {ex}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Results history */}
      {history.length > 0 && (
        <div className="space-y-4">
          {history.map(report => (
            <div key={report.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">

              {/* Report header */}
              <div className="px-6 py-4 border-b border-gray-100 flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  {report.status === 'pending' && <Loader2 className="w-4 h-4 text-blue-500 animate-spin mt-0.5 shrink-0" />}
                  {report.status === 'success' && <div className="w-2 h-2 rounded-full bg-emerald-500 mt-2 shrink-0" />}
                  {report.status === 'error'   && <div className="w-2 h-2 rounded-full bg-red-500 mt-2 shrink-0" />}
                  <p className="text-sm font-medium text-gray-900 leading-relaxed">{report.prompt}</p>
                </div>
                <div className="flex items-center gap-1 text-xs text-gray-400 shrink-0">
                  <Clock className="w-3 h-3" />
                  {new Date(report.created_at).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>

              {/* Error */}
              {report.status === 'error' && (
                <div className="px-6 py-4 bg-red-50">
                  <p className="text-sm text-red-700">{report.error}</p>
                </div>
              )}

              {/* Results table */}
              {report.status === 'success' && report.results && report.results.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        {getColumns(report.results).map(col => (
                          <th key={col} className="px-4 py-3 text-right text-xs font-semibold text-gray-600 whitespace-nowrap">
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {report.results.slice(0, 100).map((row, i) => (
                        <tr key={i} className="hover:bg-gray-50 transition-colors">
                          {getColumns(report.results!).map(col => (
                            <td key={col} className="px-4 py-3 text-gray-700 whitespace-nowrap">
                              {formatValue(row[col])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {report.results.length > 100 && (
                    <p className="px-6 py-2 text-xs text-gray-400 border-t border-gray-100">
                      מוצגות 100 שורות מתוך {report.results.length}
                    </p>
                  )}
                </div>
              )}

              {report.status === 'success' && report.results && report.results.length === 0 && (
                <div className="px-6 py-8 text-center text-sm text-gray-400">
                  לא נמצאו תוצאות לשאילתה זו
                </div>
              )}

              {/* SQL toggle */}
              {report.generated_sql && (
                <div className="border-t border-gray-100">
                  <button
                    onClick={() => setExpandedSql(prev => ({ ...prev, [report.id]: !prev[report.id] }))}
                    className="w-full px-6 py-2.5 flex items-center justify-between text-xs text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    <span>הצג SQL שנוצר</span>
                    {expandedSql[report.id] ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </button>
                  {expandedSql[report.id] && (
                    <pre className="px-6 py-4 bg-gray-900 text-green-400 text-xs overflow-x-auto font-mono leading-relaxed">
                      {report.generated_sql}
                    </pre>
                  )}
                </div>
              )}

            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {history.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <Brain className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p className="text-sm">הקלד שאלה למעלה כדי להפיק את הדוח הראשון שלך</p>
        </div>
      )}
    </div>
  );
}
